import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CalendarDays, Loader2, Lock, MapPin, Package, Store, Truck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { checkShipping, formatDkk, getEvents, getPickupPoints, postCheckout } from "@/lib/site";
import { formatEventDate } from "@/pages/MessekalenderPage";

const DELIVERY_FEE = 300;
const PAKKESHOP_BASE = 39;
const HJEMMELEVERING_BASE = 69;
const PER_KG = 15;
const FREE_MIN = 499;
const FREE_MAX_KG = 10;

export default function KassePage() {
  const { items, total } = useCart();
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "", address: "", city: "" });
  const [shipping, setShipping] = useState("afhentning");
  const [postal, setPostal] = useState("");
  const [postalInfo, setPostalInfo] = useState(null);
  const [checking, setChecking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [points, setPoints] = useState([]);
  const [servicePoint, setServicePoint] = useState(null);
  const [loadingPoints, setLoadingPoints] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getEvents().then(setEvents).catch(() => {});
  }, []);

  const today = new Date(new Date().toDateString());
  const messeEvent = events.find((e) => {
    if (!e.attending) return false;
    const days = (new Date(e.date) - today) / 86400000;
    return days >= 0 && days <= 7;
  });

  const hasTerrarium = items.some((i) => i.slug.startsWith("terrarium-"));
  const hasGecko = items.some((i) => i.slug.startsWith("gecko-"));
  const hasNoPostal = items.some((i) => i.no_postal);
  const productItems = items.filter((i) => i.slug.startsWith("product-"));
  const productWeight = productItems.reduce((a, i) => a + (i.weight_kg || 0.5) * i.qty, 0);
  const productsSubtotal = productItems.reduce((a, i) => a + i.price_dkk * i.qty, 0);
  const showPost = !hasTerrarium && !hasGecko && !hasNoPostal && productItems.length > 0;
  const showMesse = Boolean(messeEvent) && !hasTerrarium && !hasNoPostal;
  const freeShipping = productsSubtotal >= FREE_MIN && productWeight <= FREE_MAX_KG;
  const pakkeshopFee = freeShipping
    ? 0
    : PAKKESHOP_BASE + PER_KG * Math.ceil(Math.max(productWeight, 0.1));
  const hjemmeleveringFee = freeShipping
    ? 0
    : HJEMMELEVERING_BASE + PER_KG * Math.ceil(Math.max(productWeight, 0.1));
  const shippingFee =
    shipping === "levering"
      ? DELIVERY_FEE
      : shipping === "pakkeshop"
        ? pakkeshopFee
        : shipping === "hjemmelevering"
          ? hjemmeleveringFee
          : 0;

  useEffect(() => {
    const saved = localStorage.getItem("gf_customer_user");
    if (saved) {
      try {
        const u = JSON.parse(saved);
        setCustomer((c) => ({
          name: u.name || c.name,
          email: u.email || c.email,
          phone: u.phone || c.phone,
          address: u.address || c.address,
          city: u.city || c.city,
        }));
        if (u.postal_code) setPostal(u.postal_code);
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasTerrarium && shipping !== "afhentning") setShipping("afhentning");
    if (!showPost && (shipping === "pakkeshop" || shipping === "hjemmelevering"))
      setShipping("afhentning");
    if (!showMesse && shipping === "messe") setShipping("afhentning");
  }, [hasTerrarium, showPost, showMesse, shipping]);

  const deliveryOk =
    shipping !== "levering" || (postalInfo && postalInfo.eligible && postalInfo.postal === postal);
  const shopOk = shipping !== "pakkeshop" || Boolean(servicePoint);
  const canPay =
    items.length > 0 &&
    customer.name.trim().length > 0 &&
    /\S+@\S+\.\S+/.test(customer.email) &&
    customer.phone.trim().length > 0 &&
    customer.address.trim().length > 0 &&
    customer.city.trim().length > 0 &&
    postal.trim().length === 4 &&
    deliveryOk &&
    shopOk;

  const findPoints = async () => {
    setLoadingPoints(true);
    setPoints([]);
    setServicePoint(null);
    try {
      const res = await getPickupPoints(postal);
      setPoints(res);
      if (res.length === 0) toast.error("Ingen pakkeshops fundet for postnummeret.");
    } catch {
      toast.error("Kunne ikke hente pakkeshops.");
    } finally {
      setLoadingPoints(false);
    }
  };

  const checkPostal = async () => {
    setChecking(true);
    setPostalInfo(null);
    try {
      const info = await checkShipping(postal);
      setPostalInfo(info);
      if (!info.eligible) toast.error("Adressen ligger mere end 120 km fra Billund.");
    } catch {
      toast.error("Postnummeret kunne ikke findes.");
    } finally {
      setChecking(false);
    }
  };

  const pay = async () => {
    setLoading(true);
    try {
      const res = await postCheckout({
        items: items.map((i) => ({ slug: i.slug, qty: i.qty, variant: i.variant || null })),
        shipping,
        customer,
        postal_code: ["levering", "pakkeshop", "hjemmelevering"].includes(shipping) ? postal : null,
        service_point: shipping === "pakkeshop" ? servicePoint : null,
        origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Noget gik galt. Prøv igen.");
      setLoading(false);
    }
  };

  const field =
    "w-full border border-white/15 bg-ink px-4 py-3 text-sm text-cream placeholder:text-sage/50 outline-none transition-colors duration-300 focus:border-forest";

  return (
    <div data-testid="checkout-page" className="mx-auto max-w-6xl px-6 pb-24 pt-28 lg:px-16">
      <button
        data-testid="checkout-back-button"
        onClick={() => navigate("/")}
        className="mb-10 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-sage transition-colors hover:text-cream"
      >
        <ArrowLeft className="h-4 w-4" /> Tilbage til butikken
      </button>
      <h1 className="font-serif text-4xl tracking-tight text-cream sm:text-5xl">Kassen</h1>

      {items.length === 0 ? (
        <div className="mt-14 border border-white/10 bg-moss p-10">
          <p className="text-sage">Din kurv er tom.</p>
          <button
            data-testid="checkout-empty-shop-button"
            onClick={() => navigate("/")}
            className="mt-6 rounded-full bg-forest px-7 py-3 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pale hover:text-ink"
          >
            Udforsk butikken
          </button>
        </div>
      ) : (
        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-5">
          <div className="space-y-10 lg:col-span-3">
            <div className="border border-white/10 bg-moss p-8">
              <p className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-pale">
                Dine oplysninger
              </p>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <input
                  data-testid="checkout-name-input"
                  value={customer.name}
                  onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))}
                  placeholder="Fulde navn"
                  className={field}
                />
                <input
                  data-testid="checkout-email-input"
                  type="email"
                  value={customer.email}
                  onChange={(e) => setCustomer((c) => ({ ...c, email: e.target.value }))}
                  placeholder="E-mail"
                  className={field}
                />
                <input
                  data-testid="checkout-phone-input"
                  type="tel"
                  value={customer.phone}
                  onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))}
                  placeholder="Telefon"
                  className={field}
                />
                <input
                  data-testid="checkout-address-input"
                  value={customer.address}
                  onChange={(e) => setCustomer((c) => ({ ...c, address: e.target.value }))}
                  placeholder="Adresse (vej og nr.)"
                  className={field}
                />
                <input
                  data-testid="checkout-address-postal-input"
                  inputMode="numeric"
                  maxLength={4}
                  value={postal}
                  onChange={(e) => {
                    setPostal(e.target.value);
                    setPostalInfo(null);
                    setPoints([]);
                    setServicePoint(null);
                  }}
                  placeholder="Postnummer"
                  className={field}
                />
                <input
                  data-testid="checkout-city-input"
                  value={customer.city}
                  onChange={(e) => setCustomer((c) => ({ ...c, city: e.target.value }))}
                  placeholder="By"
                  className={field}
                />
              </div>
              <p className="mt-4 text-xs text-sage/70">
                Vi bruger kun dine oplysninger til levering og kontakt om ordren.
              </p>
            </div>

            <div className="border border-white/10 bg-moss p-8">
              <p className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-pale">
                Levering
              </p>
              <div className="space-y-4">
                {hasTerrarium && (
                  <p
                    data-testid="shipping-terrarium-lock"
                    className="border border-forest/40 bg-forest/10 p-4 text-xs leading-relaxed text-pale"
                  >
                    Terrarier kan kun afhentes — levering er derfor ikke mulig for denne ordre.
                  </p>
                )}
                {hasNoPostal && !hasTerrarium && (
                  <p
                    data-testid="shipping-nopostal-lock"
                    className="border border-forest/40 bg-forest/10 p-4 text-xs leading-relaxed text-pale"
                  >
                    Startpakken indeholder et terrarium — den kan derfor kun afhentes i
                    Billund eller leveres personligt (inden for 120 km).
                  </p>
                )}
                <button
                  data-testid="shipping-option-afhentning"
                  onClick={() => setShipping("afhentning")}
                  className={`flex w-full items-center justify-between border p-5 text-left transition-colors duration-300 ${
                    shipping === "afhentning"
                      ? "border-forest bg-forest/10"
                      : "border-white/15 hover:border-white/40"
                  }`}
                >
                  <span className="flex items-center gap-4">
                    <MapPin className="h-5 w-5 text-forest" />
                    <span>
                      <span className="block text-sm font-medium text-cream">
                        Afhentning i Billund
                      </span>
                      <span className="mt-1 block text-xs font-medium text-pale">
                        Fælleden 16, 7190 Billund
                      </span>
                      <span className="block text-xs text-sage">
                        Vi aftaler tidspunkt efter køb
                      </span>
                    </span>
                  </span>
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-pale">
                    Gratis
                  </span>
                </button>

                {!hasTerrarium && (
                  <button
                    data-testid="shipping-option-levering"
                    onClick={() => setShipping("levering")}
                    className={`flex w-full items-center justify-between border p-5 text-left transition-colors duration-300 ${
                      shipping === "levering"
                        ? "border-forest bg-forest/10"
                        : "border-white/15 hover:border-white/40"
                    }`}
                  >
                    <span className="flex items-center gap-4">
                      <Truck className="h-5 w-5 text-forest" />
                      <span>
                        <span className="block text-sm font-medium text-cream">
                          Personlig levering
                        </span>
                        <span className="block text-xs text-sage">
                          Inden for 120 km fra Billund
                        </span>
                      </span>
                    </span>
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-pale">
                      300 kr
                    </span>
                  </button>
                )}

                {showMesse && (
                  <button
                    data-testid="shipping-option-messe"
                    onClick={() => setShipping("messe")}
                    className={`flex w-full items-center justify-between border p-5 text-left transition-colors duration-300 ${
                      shipping === "messe"
                        ? "border-forest bg-forest/10"
                        : "border-white/15 hover:border-white/40"
                    }`}
                  >
                    <span className="flex items-center gap-4">
                      <CalendarDays className="h-5 w-5 text-forest" />
                      <span>
                        <span className="block text-sm font-medium text-cream">
                          Afhentning på messe
                        </span>
                        <span className="block text-xs font-medium text-pale">
                          {messeEvent.name} — {messeEvent.location}
                        </span>
                        <span className="block text-xs text-sage">
                          {formatEventDate(messeEvent.date)} · find os ved vores stand
                        </span>
                      </span>
                    </span>
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-pale">
                      Gratis
                    </span>
                  </button>
                )}

                {showPost && (
                  <>
                    <button
                      data-testid="shipping-option-pakkeshop"
                      onClick={() => setShipping("pakkeshop")}
                      className={`flex w-full items-center justify-between border p-5 text-left transition-colors duration-300 ${
                        shipping === "pakkeshop"
                          ? "border-forest bg-forest/10"
                          : "border-white/15 hover:border-white/40"
                      }`}
                    >
                      <span className="flex items-center gap-4">
                        <Store className="h-5 w-5 text-forest" />
                        <span>
                          <span className="block text-sm font-medium text-cream">
                            Pakkeshop (GLS)
                          </span>
                          <span className="block text-xs text-sage">
                            Vægtbaseret pris · {productWeight.toFixed(1)} kg · gratis over 499 kr
                            (op til 10 kg)
                          </span>
                        </span>
                      </span>
                      <span className="font-mono text-xs uppercase tracking-[0.2em] text-pale">
                        {pakkeshopFee === 0 ? "Gratis" : formatDkk(pakkeshopFee)}
                      </span>
                    </button>
                    <button
                      data-testid="shipping-option-hjemmelevering"
                      onClick={() => setShipping("hjemmelevering")}
                      className={`flex w-full items-center justify-between border p-5 text-left transition-colors duration-300 ${
                        shipping === "hjemmelevering"
                          ? "border-forest bg-forest/10"
                          : "border-white/15 hover:border-white/40"
                      }`}
                    >
                      <span className="flex items-center gap-4">
                        <Package className="h-5 w-5 text-forest" />
                        <span>
                          <span className="block text-sm font-medium text-cream">
                            Hjemmelevering (PostNord)
                          </span>
                          <span className="block text-xs text-sage">
                            Direkte til din dør · gratis over 499 kr (op til 10 kg)
                          </span>
                        </span>
                      </span>
                      <span className="font-mono text-xs uppercase tracking-[0.2em] text-pale">
                        {hjemmeleveringFee === 0 ? "Gratis" : formatDkk(hjemmeleveringFee)}
                      </span>
                    </button>
                  </>
                )}

                {shipping === "pakkeshop" && (
                  <div className="border border-white/10 p-5">
                    <p className="mb-3 text-xs text-sage">
                      Vi bruger postnummeret fra dine oplysninger
                      {postal ? ` (${postal})` : ""} — vælg den pakkeshop, der passer dig bedst:
                    </p>
                    <div className="flex gap-3">
                      <button
                        data-testid="checkout-find-shops-button"
                        onClick={findPoints}
                        disabled={loadingPoints || postal.length !== 4}
                        className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-cream transition-colors hover:border-forest hover:bg-forest disabled:opacity-50"
                      >
                        {loadingPoints ? <Loader2 className="h-3 w-3 animate-spin" /> : "Find pakkeshop"}
                      </button>
                    </div>
                    {points.length > 0 && (
                      <div data-testid="checkout-shop-list" className="mt-4 max-h-56 space-y-2 overflow-y-auto">
                        {points.map((p) => (
                          <button
                            key={p.id}
                            data-testid={`checkout-shop-${p.id}`}
                            onClick={() => setServicePoint(p)}
                            className={`flex w-full items-center justify-between border px-4 py-3 text-left transition-colors duration-300 ${
                              servicePoint?.id === p.id
                                ? "border-forest bg-forest/10"
                                : "border-white/15 hover:border-white/40"
                            }`}
                          >
                            <span>
                              <span className="block text-sm text-cream">{p.name}</span>
                              <span className="block text-xs text-sage">
                                {p.address}, {p.zipcode} {p.city}
                              </span>
                            </span>
                            {servicePoint?.id === p.id && (
                              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-pale">
                                Valgt
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {shipping === "levering" && (
                  <div className="border border-white/10 p-5">
                    <p className="mb-3 text-xs text-sage">
                      Vi tjekker afstanden fra Billund ud fra postnummeret i dine oplysninger
                      {postal ? ` (${postal})` : ""}:
                    </p>
                    <div className="flex gap-3">
                      <button
                        data-testid="checkout-postal-check-button"
                        onClick={checkPostal}
                        disabled={checking || postal.length !== 4}
                        className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-cream transition-colors hover:border-forest hover:bg-forest disabled:opacity-50"
                      >
                        {checking ? <Loader2 className="h-3 w-3 animate-spin" /> : "Tjek afstand"}
                      </button>
                    </div>
                    {postalInfo && (
                      <p
                        data-testid="checkout-postal-result"
                        className={`mt-3 text-xs ${postalInfo.eligible ? "text-pale" : "text-red-300"}`}
                      >
                        {postalInfo.eligible
                          ? `Afstand fra Billund: ${postalInfo.distance_km} km — levering er mulig.`
                          : `Afstand fra Billund: ${postalInfo.distance_km} km — desværre uden for vores leveringsområde.`}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="border border-white/10 bg-moss p-8">
              <p className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-pale">
                Din ordre
              </p>
              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.slug} className="flex items-center justify-between gap-4 text-sm">
                    <span className="text-cream/90">
                      {item.qty} × {item.name}
                    </span>
                    <span className="font-mono text-xs text-pale">
                      {formatDkk(item.price_dkk * item.qty)}
                    </span>
                  </div>
                ))}
              </div>
                <div className="mt-6 space-y-2 border-t border-white/10 pt-5 text-sm">
                <div className="flex justify-between text-sage">
                  <span>Levering</span>
                  <span className="font-mono text-xs">
                    {shipping === "afhentning"
                      ? "Gratis — Fælleden 16, 7190 Billund"
                      : shipping === "messe"
                        ? "Gratis — afhentning på messe"
                        : shippingFee === 0
                          ? "Gratis"
                          : formatDkk(shippingFee)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-sage">
                    I alt
                  </span>
                  <span data-testid="checkout-total" className="font-serif text-2xl text-cream">
                    {formatDkk(total + shippingFee)}
                  </span>
                </div>
              </div>
              <button
                data-testid="checkout-pay-button"
                onClick={pay}
                disabled={!canPay || loading}
                className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-forest py-4 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-pale hover:text-ink disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Lock className="h-4 w-4" />
                    Betal med Stripe
                  </>
                )}
              </button>
              <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-sage/60">
                Sikker betaling · DKK
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
